import { useState } from 'react';

const services = [
  {
    id: 'strategic-planning',
    icon: 'ri-lightbulb-flash-line',
    title: 'Strategic Planning',
    subtitle: '战略规划',
    color: 'teal',
    overview:
      'We partner with leadership teams to craft clear, actionable strategies that align organizational capabilities with market opportunities. Our structured approach transforms vision into measurable outcomes.',
    features: [
      {
        icon: 'ri-map-2-line',
        title: 'Roadmap Development',
        desc: 'Build multi-year strategic roadmaps with clear milestones, resource allocation plans, and accountability frameworks.',
      },
      {
        icon: 'ri-bar-chart-grouped-line',
        title: 'Market Analysis',
        desc: 'Deep-dive competitive intelligence and market sizing to identify high-value growth opportunities.',
      },
      {
        icon: 'ri-focus-3-line',
        title: 'Goal Alignment',
        desc: 'Cascade corporate objectives into departmental KPIs ensuring every team moves in the same direction.',
      },
      {
        icon: 'ri-refresh-line',
        title: 'Strategy Review Cycles',
        desc: 'Quarterly review cadences to adapt strategy in response to market shifts and performance data.',
      },
    ],
    stats: [
      { value: '94%', label: 'Client Goal Achievement Rate' },
      { value: '3×', label: 'Average Revenue Growth' },
      { value: '200+', label: 'Strategies Delivered' },
    ],
  },
  {
    id: 'business-advisory',
    icon: 'ri-briefcase-4-line',
    title: 'Business Advisory',
    subtitle: '商业咨询',
    color: 'emerald',
    overview:
      'Our seasoned advisors bring cross-industry expertise to guide critical business decisions — from market entry and M&A due diligence to organizational redesign and profitability improvement.',
    features: [
      {
        icon: 'ri-global-line',
        title: 'Market Entry Strategy',
        desc: 'Evaluate new markets with rigorous feasibility studies, regulatory mapping, and go-to-market playbooks.',
      },
      {
        icon: 'ri-building-2-line',
        title: 'Organizational Design',
        desc: 'Redesign structures and governance models to improve agility, accountability, and decision speed.',
      },
      {
        icon: 'ri-funds-line',
        title: 'Profitability Improvement',
        desc: 'Identify margin leakage, optimize pricing strategies, and streamline cost structures for sustainable profit growth.',
      },
      {
        icon: 'ri-handshake-line',
        title: 'M&A Advisory',
        desc: 'End-to-end support for mergers, acquisitions, and partnerships — from target screening to post-merger integration.',
      },
    ],
    stats: [
      { value: '150+', label: 'Advisory Engagements' },
      { value: '28%', label: 'Avg. Margin Improvement' },
      { value: '40+', label: 'Industries Served' },
    ],
  },
  {
    id: 'operations-management',
    icon: 'ri-settings-3-line',
    title: 'Operations Management',
    subtitle: '运营管理',
    color: 'cyan',
    overview:
      'We help organizations unlock operational excellence by eliminating inefficiencies, automating workflows, and building scalable processes that support rapid growth without sacrificing quality.',
    features: [
      {
        icon: 'ri-flow-chart',
        title: 'Process Optimization',
        desc: 'Map, analyze, and redesign end-to-end business processes using Lean and Six Sigma methodologies.',
      },
      {
        icon: 'ri-robot-line',
        title: 'Workflow Automation',
        desc: 'Identify automation opportunities and implement digital tools to reduce manual effort and human error.',
      },
      {
        icon: 'ri-stack-line',
        title: 'Supply Chain Excellence',
        desc: 'Optimize procurement, inventory, and logistics to reduce costs and improve delivery performance.',
      },
      {
        icon: 'ri-dashboard-3-line',
        title: 'Performance Dashboards',
        desc: 'Design real-time operational dashboards that give leadership instant visibility into key metrics.',
      },
    ],
    stats: [
      { value: '35%', label: 'Avg. Cost Reduction' },
      { value: '60%', label: 'Process Efficiency Gain' },
      { value: '500+', label: 'Processes Optimized' },
    ],
  },
  {
    id: 'risk-assessment',
    icon: 'ri-shield-check-line',
    title: 'Risk Assessment',
    subtitle: '风险评估',
    color: 'amber',
    overview:
      'Proactive risk identification and mitigation is at the core of resilient business strategy. We provide comprehensive risk frameworks that protect assets, ensure continuity, and build stakeholder confidence.',
    features: [
      {
        icon: 'ri-search-eye-line',
        title: 'Enterprise Risk Mapping',
        desc: 'Systematically identify and prioritize strategic, operational, financial, and reputational risks across the organization.',
      },
      {
        icon: 'ri-scales-3-line',
        title: 'Risk Quantification',
        desc: 'Apply quantitative models to assess probability and financial impact, enabling data-driven risk tolerance decisions.',
      },
      {
        icon: 'ri-first-aid-kit-line',
        title: 'Mitigation Planning',
        desc: 'Develop tailored risk response strategies including avoidance, transfer, reduction, and acceptance plans.',
      },
      {
        icon: 'ri-loop-right-line',
        title: 'Continuous Monitoring',
        desc: 'Establish ongoing risk monitoring processes with early-warning indicators and escalation protocols.',
      },
    ],
    stats: [
      { value: '99%', label: 'Business Continuity Rate' },
      { value: '70%', label: 'Risk Incident Reduction' },
      { value: '300+', label: 'Risk Assessments Completed' },
    ],
  },
  {
    id: 'compliance-support',
    icon: 'ri-file-shield-2-line',
    title: 'Compliance Support',
    subtitle: '合规支持',
    color: 'rose',
    overview:
      'Navigating complex regulatory landscapes requires specialized expertise. Our compliance team ensures your business meets all applicable standards while minimizing operational disruption and legal exposure.',
    features: [
      {
        icon: 'ri-government-line',
        title: 'Regulatory Gap Analysis',
        desc: 'Assess current practices against applicable regulations to identify compliance gaps and prioritize remediation.',
      },
      {
        icon: 'ri-file-list-3-line',
        title: 'Policy & Procedure Development',
        desc: 'Draft and implement compliance policies, procedures, and internal controls aligned with regulatory requirements.',
      },
      {
        icon: 'ri-user-star-line',
        title: 'Compliance Training',
        desc: 'Deliver targeted training programs to build a culture of compliance across all organizational levels.',
      },
      {
        icon: 'ri-audit-line',
        title: 'Audit Readiness',
        desc: 'Prepare your organization for regulatory audits with mock audits, documentation reviews, and remediation support.',
      },
    ],
    stats: [
      { value: '100%', label: 'Audit Pass Rate' },
      { value: '50+', label: 'Regulatory Frameworks' },
      { value: '180+', label: 'Compliance Programs' },
    ],
  },
];

const colorMap: Record<string, { bg: string; light: string; text: string; border: string; badge: string; stat: string }> = {
  teal:    { bg: 'bg-teal-600',    light: 'bg-teal-50',    text: 'text-teal-600',    border: 'border-teal-200',  badge: 'bg-teal-100 text-teal-700',   stat: 'text-teal-600' },
  emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', stat: 'text-emerald-600' },
  cyan:    { bg: 'bg-cyan-600',    light: 'bg-cyan-50',    text: 'text-cyan-600',    border: 'border-cyan-200',  badge: 'bg-cyan-100 text-cyan-700',   stat: 'text-cyan-600' },
  amber:   { bg: 'bg-amber-500',   light: 'bg-amber-50',   text: 'text-amber-600',   border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700',  stat: 'text-amber-600' },
  rose:    { bg: 'bg-rose-600',    light: 'bg-rose-50',    text: 'text-rose-600',    border: 'border-rose-200',  badge: 'bg-rose-100 text-rose-700',   stat: 'text-rose-600' },
};

export default function ServicesDetail() {
  const [activeId, setActiveId] = useState('strategic-planning');
  const active = services.find((s) => s.id === activeId)!;
  const c = colorMap[active.color];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Advisory Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base">
            Five integrated service pillars designed to address every dimension of your business transformation journey.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-14">
          {services.map((s) => {
            const tc = colorMap[s.color];
            const isActive = s.id === activeId;
            return (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                  isActive
                    ? `${tc.bg} text-white border-transparent shadow-md`
                    : `bg-white ${tc.text} ${tc.border} hover:${tc.light}`
                }`}
              >
                <i className={`${s.icon} text-base`}></i>
                {s.title}
              </button>
            );
          })}
        </div>

        {/* Active Service Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: Overview + Stats */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className={`rounded-2xl p-8 ${c.light} border ${c.border}`}>
              <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${c.bg} mb-5`}>
                <i className={`${active.icon} text-2xl text-white`}></i>
              </div>
              <div className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 ${c.badge}`}>
                {active.subtitle}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{active.title}</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{active.overview}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {active.stats.map((stat, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                  <div className={`text-2xl font-bold ${c.stat} mb-1`}>{stat.value}</div>
                  <div className="text-xs text-gray-500 leading-tight">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Feature Cards */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {active.features.map((feat, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl p-6 border-2 ${c.border} hover:shadow-lg transition-all duration-200 group`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${c.light} mb-4 group-hover:scale-110 transition-transform duration-200`}>
                  <i className={`${feat.icon} text-lg ${c.text}`}></i>
                </div>
                <h4 className="text-base font-semibold text-gray-900 mb-2">{feat.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className={`inline-flex flex-col sm:flex-row items-center gap-4 bg-gray-50 border ${c.border} rounded-2xl px-8 py-6`}>
            <div className="text-left">
              <p className="text-gray-900 font-semibold text-base">Ready to get started?</p>
              <p className="text-gray-500 text-sm">Talk to our advisory team about your specific needs.</p>
            </div>
            <a
              href="mailto:contact@hengtong.online"
              className={`whitespace-nowrap px-6 py-3 rounded-xl text-white text-sm font-semibold ${c.bg} hover:opacity-90 transition-opacity cursor-pointer`}
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
